import { DtoBase } from "../Utils/DtoBase";
import { ObjectSerializer } from "../Serialization/ObjectSerializer";
import { Schedule } from "../Utils/Schedule";
import { Guid } from "../Utils/Guid";
import { TimeSpan } from "../Utils/TimeSpan";
import { SchedDiscPrtgExport } from "../PRTG/SchedDiscPrtgExport";
import { AssetPandaExport } from "../Export/AssetPandaExport";
import { ITGlueExport } from "../Export/ITGlueExport";
import { HaloExport } from "../Export/HaloExport";
import { HuduExport } from "../Export/HuduExport";
import { JiraExport } from "../Export/JiraExport";
import { ValueEncoder } from "../Utils/ValueEncoder";
import { ObjUtils } from "../Utils/ObjUtils";

export enum TimeUnit { Days, Hours, Minutes };

export class NetworkDeviceMergeSetting {
	public static readonly Default = "Default";
	public static readonly Improved = "Improved";
	public static readonly Aggressive = "Aggressive";
}

export class ScheduledDiscovery extends DtoBase implements ObjectSerializer {

	public Name: string;
	public get HasName(): boolean {
		return !!this.Name;
	}
	public Schedule: Schedule;
	public Enabled: boolean;
	public DiscSettingsId: Guid;
	public get HasDiscSettingsId(): boolean {
		return !!this.DiscSettingsId;
	}
	public ClearDiscSettingsId(): void {
		this.DiscSettingsId = null;
	}
	public ExportConfigsToServer: boolean;
	public ConfigExportFolder: string;
	public get HasConfigExportFolder(): boolean {
		return !!this.ConfigExportFolder;
	}
	public CreateDateSubfolder: boolean;
	public CreateDeviceSubfolder: boolean;
	public ConfigSaveChangesOnly: boolean;
	public Keep: number;
	public DeviceExpirationValue: number;
	public DeviceExpirationUnit: TimeUnit;
	public get DeviceExpiration(): TimeSpan {
		console.assert(this.DeviceExpirationValue >= 0);
		switch (this.DeviceExpirationUnit) {
			case TimeUnit.Days:
				return TimeSpan.FromDays(this.DeviceExpirationValue);
			case TimeUnit.Hours:
				return TimeSpan.FromHours(this.DeviceExpirationValue);
			case TimeUnit.Minutes:
				return TimeSpan.FromMinutes(this.DeviceExpirationValue);
			default:
				console.assert(false);
				return new TimeSpan();
		}
	}
	public LogEvents: boolean;
	public EmailEvents: boolean;
	public EmailEventsOnChangesOnly: boolean;
	public EmailSystemRecipients: boolean;
	public EmailRecipients: string;
	public get HasEmailRecipients(): boolean {
		return !!this.EmailRecipients;
	}
	public AttachAssetsReport: boolean;
	public AttachDeviceConnectivtyReport: boolean;
	public SlackEvents: boolean;
	public SlackEventsOnChangesOnly: boolean;
	//public SlackChannels: HashSet<string>;
	public SlackWebhooks: string;
	public get HasSlackWebhooks(): boolean {
		return (!!this.SlackWebhooks);
	}
	public DesktopNotifyEvents: boolean;
	public PrtgExport: SchedDiscPrtgExport;
	public AssetPandaExport: AssetPandaExport;
	public HaloExport: HaloExport;
	public HuduExport: HuduExport;
	public ITGlueExport: ITGlueExport;
	public JiraExport: JiraExport;
	public DeviceMergeSetting: string;
	public get HasDeviceMergeSetting(): boolean {
		return !!this.DeviceMergeSetting;
	}
	public IsDiscoveryRefresh: boolean;
	public KeepStalePortReport: boolean;
	public DisableStalePorts: boolean;
	public DisableStalePortsTime: number;
	public DisableStalePortsUnit: TimeUnit;
	public ReportSubtaskStatus: boolean;

	constructor() {
		super();

		this.Schedule = new Schedule();
		this.PrtgExport = new SchedDiscPrtgExport();
		this.AssetPandaExport = new AssetPandaExport();
		this.HaloExport = new HaloExport();
		this.HuduExport = new HuduExport();
		this.ITGlueExport = new ITGlueExport();
		this.JiraExport = new JiraExport();

		this.Clear();
	}

	public Clear(): void {
		this.Name = "";
		this.Schedule.Clear();
		this.Enabled = false;
		this.ClearDiscSettingsId();
		this.ExportConfigsToServer = false;
		this.ConfigExportFolder = "";
		this.CreateDateSubfolder = false;
		this.CreateDeviceSubfolder = false;
		this.ConfigSaveChangesOnly = true;
		this.Keep = 1;
		this.DeviceExpirationValue = 7;
		this.DeviceExpirationUnit = TimeUnit.Days;
		this.LogEvents = true;
		this.EmailEvents = false;
		this.EmailEventsOnChangesOnly = false;
		this.EmailSystemRecipients = false;
		this.EmailRecipients = "";
		this.AttachAssetsReport = false;
		this.AttachDeviceConnectivtyReport = false;
		this.SlackEvents = false;
		this.SlackEventsOnChangesOnly = false;
		this.SlackWebhooks = "";
		this.DesktopNotifyEvents = false;
		this.PrtgExport.Clear();
		this.AssetPandaExport.Clear();
		this.HaloExport.Clear();
		this.HuduExport.Clear();
		this.ITGlueExport.Clear();
		this.JiraExport.Clear();
		this.DeviceMergeSetting = "";
		this.IsDiscoveryRefresh = false;
		this.KeepStalePortReport = false;
		this.DisableStalePorts = false;
		this.DisableStalePortsTime = 1;
		this.DisableStalePortsUnit = TimeUnit.Days;
		this.ReportSubtaskStatus = false;
	}

	public Equals(obj: any): boolean {
		if (!super.Equals(obj))
			return false;

		if (!(obj instanceof ScheduledDiscovery))
			return false;
		let other = (obj as ScheduledDiscovery);

		return (ObjUtils.Equals(this.Name, other.Name) &&
			ObjUtils.Equals(this.Schedule, other.Schedule) &&
			ObjUtils.Equals(this.Enabled, other.Enabled) &&
			ObjUtils.Equals(this.DiscSettingsId, other.DiscSettingsId) &&
			ObjUtils.Equals(this.ExportConfigsToServer, other.ExportConfigsToServer) &&
			ObjUtils.Equals(this.ConfigExportFolder, other.ConfigExportFolder) &&
			ObjUtils.Equals(this.CreateDateSubfolder, other.CreateDateSubfolder) &&
			ObjUtils.Equals(this.CreateDeviceSubfolder, other.CreateDeviceSubfolder) &&
			ObjUtils.Equals(this.ConfigSaveChangesOnly, other.ConfigSaveChangesOnly) &&
			ObjUtils.Equals(this.Keep, other.Keep) &&
			ObjUtils.Equals(this.DeviceExpirationValue, other.DeviceExpirationValue) &&
			ObjUtils.Equals(this.DeviceExpirationUnit, other.DeviceExpirationUnit) &&
			ObjUtils.Equals(this.LogEvents, other.LogEvents) &&
			ObjUtils.Equals(this.EmailEvents, other.EmailEvents) &&
			ObjUtils.Equals(this.EmailEventsOnChangesOnly, other.EmailEventsOnChangesOnly) &&
			ObjUtils.Equals(this.EmailSystemRecipients, other.EmailSystemRecipients) &&
			ObjUtils.Equals(this.EmailRecipients, other.EmailRecipients) &&
			ObjUtils.Equals(this.AttachAssetsReport, other.AttachAssetsReport) &&
			ObjUtils.Equals(this.AttachDeviceConnectivtyReport, other.AttachDeviceConnectivtyReport) &&
			ObjUtils.Equals(this.SlackEvents, other.SlackEvents) &&
			ObjUtils.Equals(this.SlackEventsOnChangesOnly, other.SlackEventsOnChangesOnly) &&
			ObjUtils.Equals(this.SlackWebhooks, other.SlackWebhooks) &&
			ObjUtils.Equals(this.DesktopNotifyEvents, other.DesktopNotifyEvents) &&
			ObjUtils.Equals(this.PrtgExport, other.PrtgExport) &&
			ObjUtils.Equals(this.AssetPandaExport, other.AssetPandaExport) &&
			ObjUtils.Equals(this.HaloExport, other.HaloExport) &&
			ObjUtils.Equals(this.HuduExport, other.HuduExport) &&
			ObjUtils.Equals(this.ITGlueExport, other.ITGlueExport) &&
			ObjUtils.Equals(this.JiraExport, other.JiraExport) &&
			ObjUtils.Equals(this.DeviceMergeSetting, other.DeviceMergeSetting) &&
			ObjUtils.Equals(this.IsDiscoveryRefresh, other.IsDiscoveryRefresh) &&
			ObjUtils.Equals(this.KeepStalePortReport, other.KeepStalePortReport) &&
			ObjUtils.Equals(this.DisableStalePorts, other.DisableStalePorts) &&
			ObjUtils.Equals(this.DisableStalePortsTime, other.DisableStalePortsTime) &&
			ObjUtils.Equals(this.DisableStalePortsUnit, other.DisableStalePortsUnit) &&
			this.ReportSubtaskStatus === other.ReportSubtaskStatus);
	}

	public CopyConfig(other: ScheduledDiscovery): void {
		this.Clear();
		super.CopyPersistenceProps(other);

		this.Name = other.Name;
		this.Schedule.Copy(other.Schedule);
		this.Enabled = other.Enabled;
		this.DiscSettingsId = other.DiscSettingsId;
		this.ExportConfigsToServer = other.ExportConfigsToServer;
		this.ConfigExportFolder = other.ConfigExportFolder;
		this.CreateDateSubfolder = other.CreateDateSubfolder;
		this.CreateDeviceSubfolder = other.CreateDeviceSubfolder;
		this.ConfigSaveChangesOnly = other.ConfigSaveChangesOnly;
		this.Keep = other.Keep;
		this.DeviceExpirationValue = other.DeviceExpirationValue;
		this.DeviceExpirationUnit = other.DeviceExpirationUnit;
		this.LogEvents = other.LogEvents;
		this.EmailEvents = other.EmailEvents;
		this.EmailEventsOnChangesOnly = other.EmailEventsOnChangesOnly;
		this.EmailSystemRecipients = other.EmailSystemRecipients;
		this.EmailRecipients = other.EmailRecipients;
		this.AttachAssetsReport = other.AttachAssetsReport;
		this.AttachDeviceConnectivtyReport = other.AttachDeviceConnectivtyReport;
		this.SlackEvents = other.SlackEvents;
		this.SlackEventsOnChangesOnly = other.SlackEventsOnChangesOnly;
		this.SlackWebhooks = other.SlackWebhooks;
		this.DesktopNotifyEvents = other.DesktopNotifyEvents;
		this.PrtgExport.Copy(other.PrtgExport);
		this.AssetPandaExport.Copy(other.AssetPandaExport);
		this.HaloExport.Copy(other.HaloExport);
		this.HuduExport.Copy(other.HuduExport);
		this.ITGlueExport.Copy(other.ITGlueExport);
		this.JiraExport.Copy(other.JiraExport);
		this.DeviceMergeSetting = other.DeviceMergeSetting;
		this.IsDiscoveryRefresh = other.IsDiscoveryRefresh;
		this.KeepStalePortReport = other.KeepStalePortReport;
		this.DisableStalePorts = other.DisableStalePorts;
		this.DisableStalePortsTime = other.DisableStalePortsTime;
		this.DisableStalePortsUnit = other.DisableStalePortsUnit;
		this.ReportSubtaskStatus = other.ReportSubtaskStatus;
	}

	public HasConfigChanges(other: ScheduledDiscovery): boolean {
		if (this.Name != other.Name)
			return true;
		if (!this.Schedule.Equals(other.Schedule))
			return true;
		if (this.Enabled != other.Enabled)
			return true;
		if (this.DiscSettingsId != other.DiscSettingsId)
			return true;
		if (this.ExportConfigsToServer != other.ExportConfigsToServer)
			return true;
		if (this.ConfigExportFolder != other.ConfigExportFolder)
			return true;
		if (this.CreateDateSubfolder != other.CreateDateSubfolder)
			return true;
		if (this.CreateDeviceSubfolder != other.CreateDeviceSubfolder)
			return true;
		if (this.ConfigSaveChangesOnly != other.ConfigSaveChangesOnly)
			return true;
		if (this.Keep != other.Keep)
			return true;
		if (this.DeviceExpirationValue != other.DeviceExpirationValue)
			return true;
		if (this.DeviceExpirationUnit != other.DeviceExpirationUnit)
			return true;
		if (this.LogEvents != other.LogEvents)
			return true;
		if (this.EmailEvents != other.EmailEvents)
			return true;
		if (this.EmailEventsOnChangesOnly != other.EmailEventsOnChangesOnly)
			return true;
		if (this.EmailSystemRecipients != other.EmailSystemRecipients)
			return true;
		if (this.EmailRecipients != other.EmailRecipients)
			return true;
		if (this.AttachAssetsReport != other.AttachAssetsReport)
			return true;
		if (this.AttachDeviceConnectivtyReport != other.AttachDeviceConnectivtyReport)
			return true;
		if (this.SlackEvents != other.SlackEvents)
			return true;
		if (this.SlackEventsOnChangesOnly != other.SlackEventsOnChangesOnly)
			return true;
		if (this.SlackWebhooks != other.SlackWebhooks)
			return true;
		if (this.DesktopNotifyEvents != other.DesktopNotifyEvents)
			return true;
		if (!this.PrtgExport.Equals(other.PrtgExport))
			return true;
		if (!this.AssetPandaExport.Equals(other.AssetPandaExport))
			return true;
		if (!this.HaloExport.Equals(other.HaloExport))
			return true;
		if (!this.HuduExport.Equals(other.HuduExport))
			return true;
		if (!this.ITGlueExport.Equals(other.ITGlueExport))
			return true;
		if (!this.JiraExport.Equals(other.JiraExport))
			return true;
		if (this.DeviceMergeSetting != other.DeviceMergeSetting)
			return true;
		if (this.IsDiscoveryRefresh != other.IsDiscoveryRefresh)
			return true;
		if (this.KeepStalePortReport != other.KeepStalePortReport)
			return true;
		if (this.DisableStalePorts != other.DisableStalePorts)
			return true;
		if (this.DisableStalePortsTime != other.DisableStalePortsTime)
			return true;
		if (this.DisableStalePortsUnit != other.DisableStalePortsUnit)
			return true;
		if (this.ReportSubtaskStatus != other.ReportSubtaskStatus)
			return true;

		return false;
	}

	public Write(obj: any, context: any): void {
		obj.scheduled_discovery = {};
		obj = obj.scheduled_discovery;

		super.Write(obj, context);

		if (this.HasName)
			obj.name = this.Name;

		this.Schedule.Write(obj, context);

		obj.enabled = ValueEncoder.EncodeBoolean(this.Enabled);

		if (this.HasDiscSettingsId)
			obj.discovery_settings_id = ValueEncoder.EncodeGuid(this.DiscSettingsId);

		obj.export_configs_to_server = ValueEncoder.EncodeBoolean(this.ExportConfigsToServer);

		if (this.HasConfigExportFolder)
			obj.config_export_folder = this.ConfigExportFolder;

		obj.create_date_subfolder = ValueEncoder.EncodeBoolean(this.CreateDateSubfolder);
		obj.create_device_subfolder = ValueEncoder.EncodeBoolean(this.CreateDeviceSubfolder);
		obj.config_save_changes_only = ValueEncoder.EncodeBoolean(this.ConfigSaveChangesOnly);
		obj.keep = ValueEncoder.EncodeInteger(this.Keep);
		obj.device_expiration_value = ValueEncoder.EncodeInteger(this.DeviceExpirationValue);
		obj.device_expiration_unit = TimeUnit[this.DeviceExpirationUnit];
		obj.log_events = ValueEncoder.EncodeBoolean(this.LogEvents);
		obj.mail_events = ValueEncoder.EncodeBoolean(this.EmailEvents);
		obj.mail_events_changes_only = ValueEncoder.EncodeBoolean(this.EmailEventsOnChangesOnly);
		obj.email_system_recipients = ValueEncoder.EncodeBoolean(this.EmailSystemRecipients);
		if (this.HasEmailRecipients)
			obj.email_recipients = this.EmailRecipients;
		obj.email_assets_report = ValueEncoder.EncodeBoolean(this.AttachAssetsReport);
		obj.email_deviceconnectivity_report = ValueEncoder.EncodeBoolean(this.AttachDeviceConnectivtyReport);
		obj.slack_events = ValueEncoder.EncodeBoolean(this.SlackEvents);
		obj.slack_events_changes_only = ValueEncoder.EncodeBoolean(this.SlackEventsOnChangesOnly);
		if (this.HasSlackWebhooks) {
			obj.webhooks = this.SlackWebhooks;
		}
		obj.desktop_notify_events = ValueEncoder.EncodeBoolean(this.DesktopNotifyEvents);

		this.PrtgExport.Write(obj, context);
		this.AssetPandaExport.Write(obj, context);
		this.HaloExport.Write(obj, context);
		this.HuduExport.Write(obj, context);
		this.ITGlueExport.Write(obj, context);
		this.JiraExport.Write(obj, context);

		if (this.HasDeviceMergeSetting)
			obj.device_merge = this.DeviceMergeSetting;

		obj.disc_refresh = ValueEncoder.EncodeBoolean(this.IsDiscoveryRefresh);
		obj.keep_stale_port_report = ValueEncoder.EncodeBoolean(this.KeepStalePortReport);
		obj.disable_stale_ports = ValueEncoder.EncodeBoolean(this.DisableStalePorts);
		obj.disable_stale_ports_time = ValueEncoder.EncodeInteger(this.DisableStalePortsTime);
		obj.disable_stale_ports_uom = TimeUnit[this.DisableStalePortsUnit];
		obj.report_subtask_status = ValueEncoder.EncodeBoolean(this.ReportSubtaskStatus);
	}

	public Read(obj: any, context: any): void {
		this.Clear();

		if (!obj.scheduled_discovery)
			throw new Error();
		obj = obj.scheduled_discovery;

		super.Read(obj, context);

		if (obj.name !== undefined)
			this.Name = obj.name;

		if (obj.schedule !== undefined)
			this.Schedule.Read(obj, context);

		if (obj.enabled !== undefined)
			this.Enabled = ValueEncoder.DecodeBoolean(obj.enabled);

		if (obj.discovery_settings_id !== undefined)
			this.DiscSettingsId = ValueEncoder.DecodeGuid(obj.discovery_settings_id);

		if (obj.export_configs_to_server !== undefined)
			this.ExportConfigsToServer = ValueEncoder.DecodeBoolean(obj.export_configs_to_server);

		if (obj.config_export_folder !== undefined)
			this.ConfigExportFolder = obj.config_export_folder;

		// Account for corrected misspelling in discovery engine. 
		if (obj.config_date_subfolder !== undefined)
			this.CreateDateSubfolder = ValueEncoder.DecodeBoolean(obj.create_date_subfolder);
		if (obj.config_device_subfolder !== undefined)
			this.CreateDeviceSubfolder = ValueEncoder.DecodeBoolean(obj.config_device_subfolder);
		if (obj.create_device_subfolder !== undefined)
			this.CreateDeviceSubfolder = ValueEncoder.DecodeBoolean(obj.create_device_subfolder);

		if (obj.config_save_changes_only !== undefined)
			this.ConfigSaveChangesOnly = ValueEncoder.DecodeBoolean(obj.config_save_changes_only);

		if (obj.keep !== undefined)
			this.Keep = ValueEncoder.DecodeInteger(obj.keep);

		if (obj.device_expiration_value !== undefined)
			this.DeviceExpirationValue = ValueEncoder.DecodeInteger(obj.device_expiration_value);

		if (obj.device_expiration_unit !== undefined)
			this.DeviceExpirationUnit = TimeUnit[<string>obj.device_expiration_unit];

		if (obj.log_events !== undefined)
			this.LogEvents = ValueEncoder.DecodeBoolean(obj.log_events);

		if (obj.mail_events !== undefined)
			this.EmailEvents = ValueEncoder.DecodeBoolean(obj.mail_events);

		if (obj.mail_events_changes_only !== undefined)
			this.EmailEventsOnChangesOnly = ValueEncoder.DecodeBoolean(obj.mail_events_changes_only);

		if (obj.email_system_recipients !== undefined)
			this.EmailSystemRecipients = ValueEncoder.DecodeBoolean(obj.email_system_recipients);

		if (obj.email_recipients !== undefined)
			this.EmailRecipients = obj.email_recipients;

		if (obj.email_assets_report !== undefined)
			this.AttachAssetsReport = ValueEncoder.DecodeBoolean(obj.email_assets_report);

		if (obj.email_deviceconnectivity_report !== undefined)
			this.AttachDeviceConnectivtyReport = ValueEncoder.DecodeBoolean(obj.email_deviceconnectivity_report);

		if (obj.slack_events !== undefined)
			this.SlackEvents = ValueEncoder.DecodeBoolean(obj.slack_events);

		if (obj.slack_events_changes_only !== undefined)
			this.SlackEventsOnChangesOnly = ValueEncoder.DecodeBoolean(obj.slack_events_changes_only);

		if (obj.webhooks !== undefined)
			this.SlackWebhooks = obj.webhooks;

		if (obj.desktop_notify_events !== undefined)
			this.DesktopNotifyEvents = ValueEncoder.DecodeBoolean(obj.desktop_notify_events);

		if (obj.sched_disc_prtg_export !== undefined)
			this.PrtgExport.Read(obj, context);

		if (obj.assetpanda_export !== undefined)
			this.AssetPandaExport.Read(obj, context);

		if (obj.halo_export !== undefined)
			this.HaloExport.Read(obj, context);

		if (obj.hudu_export !== undefined)
			this.HuduExport.Read(obj, context);

		if (obj.itglue_export !== undefined)
			this.ITGlueExport.Read(obj, context);

		if (obj.jira_export !== undefined)
			this.JiraExport.Read(obj, context);

		if (obj.device_merge !== undefined)
			this.DeviceMergeSetting = obj.device_merge;

		if (obj.disc_refresh !== undefined)
			this.IsDiscoveryRefresh = ValueEncoder.DecodeBoolean(obj.disc_refresh);

		if (obj.keep_stale_port_report !== undefined)
			this.KeepStalePortReport = ValueEncoder.DecodeBoolean(obj.keep_stale_port_report);

		if (obj.disable_stale_ports !== undefined)
			this.DisableStalePorts = ValueEncoder.DecodeBoolean(obj.disable_stale_ports);

		if (obj.disable_stale_ports_time !== undefined)
			this.DisableStalePortsTime = ValueEncoder.DecodeInteger(obj.disable_stale_ports_time);

		this.DisableStalePortsUnit = TimeUnit.Days;
		if (obj.disable_stale_ports_uom !== undefined) {
			if ((obj.disable_stale_ports_uom == "days") || (obj.disable_stale_ports_uom == "Days")) {
				this.DisableStalePortsUnit = TimeUnit.Days;
			}
			if ((obj.disable_stale_ports_uom == "hours") || (obj.disable_stale_ports_uom == "Hours")) {
				this.DisableStalePortsUnit = TimeUnit.Days;
			}
		}

		if (obj.report_subtask_status !== undefined)
			this.ReportSubtaskStatus = ValueEncoder.DecodeBoolean(obj.report_subtask_status);
	}

}

